import React, { useState, useEffect } from 'react';

import { loginRequest } from "config/authConfig";
import { apiConfig } from 'config/apiConfig'

import useFetchWithMsal from 'hooks/useFetchWithMsal';

// @mui material components
import Grid from "@mui/material/Grid";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

import wavesWhite from "assets/images/shapes/waves-white.svg";


import { ProgressIndicator, useProgressIndicator } from 'components/Elements/ProgressIndicator';

import MiniActionCard from "components/Elements/MiniActionCard";


import { useCardroomContext } from 'features';
import { Link, useNavigate } from "react-router-dom";
import ClubReport from 'layouts/Club/components/Reports/ClubReport';
import UnionReport from 'layouts/Club/components/Reports/UnionReport';
import ReportUtilities from '../Navigation';
import { useLayoutEffect } from 'react';
import CardroomLogo from 'components/Elements/CardroomLogo';
import AgencyReport from 'layouts/Club/components/Reports/AgencyReport';

import CurrencyConverterWidget from 'components/Elements/CurrencyConverter';
import { useCurrencyConverter } from 'components/Elements/CurrencyConverter/currency';
import BookReport from 'layouts/Club/components/Reports/BookReport';


const ReportDetails = ({ cardroom, club, union, agency, book, reportingPeriod, audience }) => {

    function formatStartEndDates(start, end) {
        let result = "";
        if (start) {
            let sdt = new Date(Date.parse(start));
            result += sdt.toLocaleDateString();
        }

        if (end) {
            let edt = new Date(Date.parse(end));
            if (result) result += " - ";
            result += edt.toLocaleDateString();
        }

        return result;
    }

    const clubRequest = useFetchWithMsal({
        scopes: apiConfig.club.scopes.read,
    });

    const bookRequest = useFetchWithMsal({
        scopes: apiConfig.book.scopes.read,
    });

    const [retryCounter, setRetryCounter] = useState(0);

    function handleRetry() {
        setRetryCounter(retryCounter + 1);
    }
    const [showProgress, progressIndicatorProps] = useProgressIndicator("wait", "Loading report summary...", handleRetry)

    const [reportSummary, setReportSummary] = useState(null);
    const [lastClub, setLastClub] = useState(club);
    const [lastUnion, setLastUnion] = useState(union);
    const [lastAgency, setLastAgency] = useState(agency);
    const [lastBook, setLastBook] = useState(book);

    useEffect(() => {
        if (lastClub != club) {
            setReportSummary(null);
            setLastClub(club);
        }
    }, [lastClub, club]);

    useEffect(() => {
        if (lastUnion != union) {
            setReportSummary(null);
            setLastUnion(union);
        }
    }, [lastUnion, union]);

    useEffect(() => {
        if (lastAgency != agency) {
            setReportSummary(null);
            setLastAgency(agency);
        }
    }, [lastAgency, agency]);

    useEffect(() => {
        if (lastBook != book) {
            setReportSummary(null);
            setLastBook(book);
        }
    }, [lastBook, book]);

    const [currencyConverter, currencies] = useCurrencyConverter(cardroom);

    function getDefaultCurrency() {
        var currency = "USD";
        switch (audience) {
            case "union":
                currency = union.currency;
                break;
            case "club":
                currency = club.currency;
                break;
            case "agency":
                currency = agency.currency;
                break;
            case "sportsbook":
                currency = book.currency;
                break;
        }
        return currencyConverter.getCurrencyFrom(currency);
    }

    const [currency, setCurrency] = useState(getDefaultCurrency());

    function onCurrencySelectionChanged(c) {
        setCurrency(c);
    }

    const [currencySymbol, setCurrencySymbol] = useState(null);
    function isCurrencyMaskingEnabled() {
        if (!context) return false;
        return context.isEnabled(features.currency.mask, 0);
    }

    useEffect(() => {
        if (true === isCurrencyMaskingEnabled()) {
            setCurrencySymbol("");
            return;
        }

        if (currency) setCurrencySymbol(currency.symbol);
    }, [currency]);


    //const supportedCurrencies = [currencies.USD, currencies.GBP, currencies.CAD, currencies.AUD, currencies.EUR, currencies.JPY, currencies.CNY, currencies.KRW, currencies.BRL, currencies.INR, currencies.BTC, currencies.ETH];

    //const [openCurrencyDrawer, currencyDrawerProperties] = useDrawer();


    function zero() {
        return parseFloat(0).toFixed(2);
    }

    function formatDecimalValue(v) {
        if (!v) return zero();

        var v = currencyConverter.convertFromUSD(v, currency);

        return v.toFixed(2);
    }


    useEffect(() => {
        if (!reportSummary) {
            progressIndicatorProps.setMode("wait");
            progressIndicatorProps.setMessage("Loading report summary...")
            showProgress();

            //TODO: INJECT LOGIC TO FETCH BOOK REPORT SUMMARY


            //[HttpGet("casino/{casinoId:Guid}/reports/{reportingPeriodId:Guid}/summary", Name = "GetReportingPeriodSummary")]
            let url = apiConfig.club.endpoint + "/reporting/casino/" + cardroom.id + "/reports/" + reportingPeriod.id + "/summary";
            var execute = clubRequest.execute;
            if (club || union || agency) {
                url += "?";
                if (club) {
                    url += "clubId=" + club.id;
                }
                if (union) {
                    if (club) url += "&";
                    url += "unionId=" + union.id;
                }

                if (agency) {
                    if (club || union) url += "&";
                    url += "agencyId=" + agency.id;
                }
            }

            if (book) {
                url = apiConfig.book.endpoint + "/report/" + book.id + "/casino/" + cardroom.id + "/reports/" + reportingPeriod.id + "/summary";
                execute = bookRequest.execute;
            }

            execute("GET", url).then((response) => {
                if (response) {
                    if (response.status && response.errors) {
                        throw new Error(response.errors);
                    }
                    setReportSummary(response);
                }
                progressIndicatorProps.close();
            }).catch((ex) => {
                progressIndicatorProps.setMode("errorWithRetry");
                progressIndicatorProps.setMessage("Unable to load report summary, please try again...")
            });
        }
    }, [clubRequest.execute, bookRequest.execute, reportSummary, lastClub, lastUnion, lastAgency, lastBook, retryCounter]); //lastClub/lastUnion are used to forece data fetch when naving from one union to another and back

    const [context, actions, features] = useCardroomContext(cardroom);

    const navigate = useNavigate();
    function onNavigationRequested(e) {
        if (e && e.detail && e.detail.path) {
            setLastClub(null);
            setLastAgency(null);
            setLastUnion(null);

            setReportSummary(null);
            navigate(e.detail.path, { state: e.detail.state });
        }
    }

    useLayoutEffect(() => {
        //const [size, setSize] = useState([0, 0]);

        ReportUtilities.subscribe(onNavigationRequested);
        return () => ReportUtilities.unsubscribe(onNavigationRequested);
    }, []);

    function navigateToDetailPage() {
        let path = "/dashboards/union";
        if (agency) {
            path = "/dashboards/agency";
        }
        else if (club) {
            path = "/dashboards/club";
        }
        else if (book) {
            path = "/dashboards/book";
        }

        navigate(path, { state: { cardroom: cardroom } });
    }

    return <>
        {reportSummary && !progressIndicatorProps.visible ? <>
            <SoftBox >
                <SoftBox
                    position="relative"
                    bgColor="secondary"
                    py={3}
                    px={{ xs: 3, sm: 6, md: 1 }}
                    mt={3}
                    borderRadius="xl"
                    variant="gradient" >

                    <SoftBox
                        component="img"
                        src={wavesWhite}
                        alt="pattern-line"
                        width="100%"
                        position="absolute"
                        left="0"
                        top="0"
                        opacity={0.4}
                    />

                    <Grid container alignItems="center" position="relative">

                        <CardroomLogo cardroom={cardroom} club={club} union={union} agency={agency} book={book} canOpenProperties={false} customAction={navigateToDetailPage} />

                        <Grid item xs={12} lg={11}>
                            <Grid container>
                                <Grid item xs={12} textAlign={"center"} mr="auto" ml="auto" width="max-content">
                                    <SoftTypography variant="h4" fontWeight="medium" mt={{ md: -3.25, xs: 0 }} color="light">
                                        {audience === "union" && union ? union.name : null}
                                        {audience === "club" && club ? club.name : null}
                                        {audience === "agency" && agency ? agency.appAgentId : null}
                                        {audience === "sportsbook" && book ? book.name : null}
                                    </SoftTypography>
                                </Grid>
                            </Grid>
                            <Grid container>
                                <Grid item xs={6} lg={3} p={1} >
                                    <MiniActionCard
                                        bgColor="dark"
                                        title={{ text: "Reporting Period", fontWeight: "medium" }}
                                        count={{ text: reportingPeriod ? formatStartEndDates(reportingPeriod.startTime, reportingPeriod.endTime) : "" }}
                                        icon={{ component: "calendar_month_outlined" }}
                                        action={null}
                                    />

                                </Grid>
                                <Grid item xs={6} lg={3} p={1}>
                                    <MiniActionCard
                                        bgColor="dark"
                                        title={{ text: audience === "union" || audience === "agency" ? "Clubs" : "Settlement", fontWeight: "medium" }}
                                        count={{
                                            text: audience === "union" || audience === "agency" ? reportSummary.clubCount : formatDecimalValue(reportSummary.settlementAmount),
                                            prefix: audience === "union" || audience === "agency" ? "" : currencySymbol,
                                            color: audience === "union" || audience === "agency" ? "success" : book ? ((reportSummary.totalWon + reportSummary.totalLost) > 0 ? "success" : "error") : reportSummary.settlementAmount > 0 ? "success" : "error",
                                            useNumberFormatter: (audience === "club" || audience === "sportsbook")
                                        }}
                                        icon={{ component: audience === "union" || audience === "agency" ? "nightlife" : "attach_money" }}
                                    />
                                </Grid>
                                <Grid item xs={6} lg={3} p={1}>
                                    <MiniActionCard
                                        bgColor="dark"
                                        title={{ text: "Players", fontWeight: "medium" }}
                                        count={{ text: reportSummary.playerCount, color: "success", useNumberFormatter: true }}
                                        icon={{ component: "person" }}
                                    />
                                </Grid>
                                <Grid item xs={6} lg={3} p={1}>
                                    <MiniActionCard
                                        bgColor="dark"
                                        title={{ text: "Agents", fontWeight: "medium" }}
                                        count={{ text: reportSummary.agentCount, color: "success", useNumberFormatter: true }}
                                        icon={{ component: "support_agent" }}
                                    />

                                </Grid>
                            </Grid>
                        </Grid>

                    </Grid>
                </SoftBox>
            </SoftBox>
            <SoftBox mb={3} p={1}>
                <Grid container spacing={1.5} alignItems="center">
                    <Grid item xs={12} md={12} lg={12} sx={{ ml: "auto", mr: "auto" }}>
                        {reportSummary ?
                            <SoftBox>
                                {audience === "club" ?
                                    <ClubReport cardroom={cardroom} club={club} reportingPeriod={reportingPeriod} summary={reportSummary} currency={currency} currencySymbol={currencySymbol} currencyConverter={currencyConverter} /> : null}
                                {audience === "union" ?
                                    <UnionReport cardroom={cardroom} union={union} reportingPeriod={reportingPeriod} summary={reportSummary} currency={currency} currencySymbol={currencySymbol} currencyConverter={currencyConverter} /> : null}
                                {audience === "agency" ?
                                    <AgencyReport cardroom={cardroom} agency={agency} reportingPeriod={reportingPeriod} summary={reportSummary} currency={currency} currencySymbol={currencySymbol} currencyConverter={currencyConverter} /> : null}
                                {audience === "sportsbook" ?
                                    <BookReport cardroom={cardroom} book={book} reportingPeriod={reportingPeriod} summary={reportSummary} currency={currency} currencySymbol={currencySymbol} currencyConverter={currencyConverter} /> : null}

                            </SoftBox> : null}
                    </Grid>
                    <Grid item xs={12}>

                        <CurrencyConverterWidget cardroom={cardroom} selectedCurrency={currency} onCurrencyChanged={onCurrencySelectionChanged} />

                    </Grid>
                </Grid>
            </SoftBox></> : <ProgressIndicator {...progressIndicatorProps} />
        }
    </>;
}

export default ReportDetails;